// ============================================================================
// OUTSTANDING DISPLAY — the shared way to show a party's position (ADR-0042 §6).
//
// Every screen that shows "what this party owes" should use this rather than
// computing its own total, because the figure is not just a sum: it is bounded
// by what the user is allowed to see, and the wider figure is "across YOUR
// business units", never the entity's true total. A screen that derived its own
// number would eventually disagree with the list it opens — and would disagree
// differently for each user.
//
// Both sides come back from one call, because "outstanding" means opposite
// things in different places: a receipt cares what they owe us, a payment cares
// what we owe them.
// ============================================================================

import React, { useEffect, useState } from 'react';
import { outstandingService } from 'api/services';
import type { PartyOutstandingDto, OutstandingSlice } from 'api/types';

/**
 * Which side to show. `auto` picks whichever side has a balance, preferring what we owe;
 * `both` shows each side that has anything — for screens about the party rather than a
 * transaction, where either direction is news. `both` still costs one call.
 */
export type OutstandingSide = 'receivable' | 'payable' | 'auto' | 'both';

export interface OutstandingDisplayProps {
  partyId?: string | null;
  /** What the screen is about. Payments/refunds want `payable`; receipts/AR want `receivable`. */
  side?: OutstandingSide;
  /**
   * The business unit the near figure should describe. Defaults to the user's session unit.
   * Pass it wherever the screen's work belongs to another unit — otherwise the figure describes
   * one unit while the list beside it describes another, which is the disagreement this component
   * exists to prevent (ADR-0042 §6).
   */
  businessUnitId?: string | null;
  /** `inline` for a one-line strip beside a picker; `card` for a bordered block. */
  variant?: 'inline' | 'card';
  /** Hide entirely when the party has nothing outstanding, rather than showing zeroes. */
  hideWhenEmpty?: boolean;
  /** Bump to force a refetch after posting something that changes the balance. */
  refreshKey?: number | string;
}

const money = (n: number) =>
  n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const SIDE_LABEL: Record<'receivable' | 'payable', string> = {
  receivable: 'Owes us',
  payable: 'We owe',
};

/**
 * Which sides to render. `auto` collapses to one; `both` returns each side that has something,
 * so a party who is only ever a customer never grows an empty "we owe" line.
 */
function resolveSides(data: PartyOutstandingDto, side: OutstandingSide): Array<'receivable' | 'payable'> {
  if (side === 'receivable' || side === 'payable') return [side];
  if (side === 'auto') return [data.payable.hasAny ? 'payable' : 'receivable'];
  return (['receivable', 'payable'] as const).filter(s => data[s].hasAny);
}

/**
 * The wider figure's label has to tell the truth. When the user can see every unit in the entity
 * it genuinely is the entity total; when they cannot, naming it one would overstate what they are
 * looking at (ADR-0042 §6).
 */
function widerLabel(data: PartyOutstandingDto): string {
  if (!data.isScopeNarrowed) return 'Entity total';
  return `Across your ${data.accessibleBusinessUnitCount} business units`;
}

export const OutstandingDisplay: React.FC<OutstandingDisplayProps> = ({
  partyId, side = 'auto', businessUnitId, variant = 'inline', hideWhenEmpty = true, refreshKey,
}) => {
  const [data, setData] = useState<PartyOutstandingDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!partyId) { setData(null); setFailed(false); return; }
    setLoading(true); setFailed(false);
    outstandingService.getForParty(partyId, businessUnitId)
      .then(d => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) { setData(null); setFailed(true); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [partyId, businessUnitId, refreshKey]);

  if (!partyId) return null;

  if (loading) {
    return <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Loading position…</span>;
  }

  // A failed lookup must not read as "nothing outstanding" — that is a number, and a wrong one.
  if (failed) {
    return <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Position unavailable</span>;
  }

  if (!data) return null;

  const sides = resolveSides(data, side);
  const worthShowing = sides.filter(s => data[s].hasAny || !hideWhenEmpty);
  if (worthShowing.length === 0) return null;

  const ccy = data.baseCurrencyCode || '';

  /** One side, rendered at both levels. The wider line is suppressed when it adds nothing. */
  const renderSide = (shown: 'receivable' | 'payable') => {
    const slice: OutstandingSlice = data[shown];
    const showWider = slice.acrossAccessibleUnitsCount > slice.inCurrentBusinessUnitCount
                   || slice.acrossAccessibleUnits !== slice.inCurrentBusinessUnit;
    const tone = shown === 'payable' ? 'var(--danger, #dc2626)' : 'var(--success, #16a34a)';
    const currentLabel = data.currentBusinessUnitName
      ? `${SIDE_LABEL[shown]} in ${data.currentBusinessUnitName}`
      : SIDE_LABEL[shown];

    if (variant === 'card') {
      return (
        <div key={shown} style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'baseline' }}>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{currentLabel}</span>
            <strong style={{ fontVariantNumeric: 'tabular-nums', color: tone }}>
              {ccy} {money(slice.inCurrentBusinessUnit)}
            </strong>
          </div>
          {showWider && (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'baseline' }}>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{widerLabel(data)}</span>
              <span style={{ fontSize: '0.85rem', fontVariantNumeric: 'tabular-nums', color: 'var(--text-secondary)' }}>
                {ccy} {money(slice.acrossAccessibleUnits)}
              </span>
            </div>
          )}
          <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>
            {slice.acrossAccessibleUnitsCount} open item{slice.acrossAccessibleUnitsCount === 1 ? '' : 's'}
          </div>
        </div>
      );
    }

    return (
      <span key={shown} style={{ display: 'inline-flex', gap: '0.6rem', alignItems: 'baseline', flexWrap: 'wrap' }}>
        <span style={{ color: 'var(--text-secondary)' }}>
          {currentLabel}{' '}
          <strong style={{ fontVariantNumeric: 'tabular-nums', color: tone }}>{ccy} {money(slice.inCurrentBusinessUnit)}</strong>
        </span>
        {showWider && (
          <span style={{ color: 'var(--text-muted)' }}>
            · {widerLabel(data)}{' '}
            <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{ccy} {money(slice.acrossAccessibleUnits)}</strong>
          </span>
        )}
      </span>
    );
  };

  if (variant === 'card') {
    return (
      <div style={{
        border: '1px solid var(--border-color)', borderRadius: '0.5rem',
        background: 'var(--bg-secondary)', padding: '0.6rem 0.8rem',
        display: 'flex', flexDirection: 'column', gap: '0.6rem',
      }}>
        {worthShowing.map(renderSide)}
      </div>
    );
  }

  return (
    <span style={{ display: 'inline-flex', gap: '0.75rem', alignItems: 'baseline', flexWrap: 'wrap', fontSize: '0.75rem' }}>
      {worthShowing.map(renderSide)}
    </span>
  );
};

export default OutstandingDisplay;
